import { useQuery } from "@tanstack/react-query";
import {
  CalendarClock,
  CalendarOff,
  Clock,
  ListTodo,
  RotateCcw,
  ShieldCheck,
  Users,
} from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../app/AuthContext";
import { api } from "../shared/api/client";
import { Card, CardBody, CardHeader, PageHeader, StatusBadge } from "../shared/ui";
import type {
  ApprovalQueueEntry,
  LeaveView,
  PageResponse,
  ReopenView,
  ScheduleChangeView,
  TimesheetView,
  WorkItemSummary,
} from "../shared/types/api";
import { formatHoursAsTime, isoDate } from "./timesheets/timesheetVocabulary";
import { STATUS_LABELS, TYPE_LABELS } from "./workitems/workItemVocabulary";

/**
 * The landing page.
 *
 * <p>Two questions, in the order people actually ask them: what is mine, and what is waiting on
 * me. Everything below is real data from the server — there are no invented metrics and no tiles
 * that would read as meaningful when nothing has been recorded.
 *
 * <p>The "waiting on you" panel only appears for people who can act on something. Showing an
 * empty approvals queue to a developer teaches them to ignore that half of the page.
 */
export function HomePage() {
  const { user, can } = useAuth();

  /* Whichever period today falls in — the server works out the boundaries, so this card says the
     same thing as the timesheet page whether the organisation submits weekly or monthly. */
  const today = isoDate(new Date());

  const myWork = useQuery({
    queryKey: ["my-work"],
    queryFn: () => api.get<WorkItemSummary[]>("/api/v1/work-items/my"),
    enabled: can("WORK_ITEM_VIEW"),
  });

  const timesheet = useQuery({
    queryKey: ["timesheet", "mine", "today", today],
    queryFn: () => api.post<TimesheetView>(`/api/v1/timesheets/period?on=${today}`),
    enabled: can("TIMESHEET_VIEW"),
  });

  const approvals = useQuery({
    queryKey: ["timesheets", "awaiting-me"],
    queryFn: () => api.get<ApprovalQueueEntry[]>("/api/v1/timesheets/awaiting-me"),
    enabled: can("TIMESHEET_APPROVE"),
  });

  const reopens = useQuery({
    queryKey: ["timesheets", "reopen-requests"],
    queryFn: () => api.get<ReopenView[]>("/api/v1/timesheets/reopen-requests"),
    enabled: can("TIMESHEET_REOPEN"),
  });

  const scheduleChanges = useQuery({
    queryKey: ["schedule-changes", "awaiting-approval"],
    queryFn: () => api.get<ScheduleChangeView[]>("/api/v1/schedule-changes/awaiting-approval"),
    enabled: can("SCHEDULE_CHANGE_APPROVE"),
  });

  const leave = useQuery({
    queryKey: ["leave", "REQUESTED"],
    queryFn: () => api.get<PageResponse<LeaveView>>("/api/v1/leave?status=REQUESTED&size=50"),
    enabled: can("LEAVE_APPROVE"),
  });

  if (!user) {
    return null;
  }

  const firstName = user.fullName.split(" ")[0];

  const open = myWork.data ?? [];
  const overdue = open.filter((item) => item.scheduleVarianceDays > 0);
  const blocked = open.filter((item) => item.openBlockerCount > 0);

  const waiting: WaitingItem[] = [
    {
      key: "timesheets",
      count: approvals.data?.length ?? 0,
      label: "timesheet",
      description: "submitted and waiting on your decision",
      to: "/timesheets/approvals",
      icon: <Clock className="h-4 w-4" />,
    },
    {
      key: "reopens",
      count: reopens.data?.length ?? 0,
      label: "correction request",
      description: "on a period that has already been settled",
      to: "/timesheets/approvals",
      icon: <RotateCcw className="h-4 w-4" />,
    },
    {
      key: "schedule",
      count: scheduleChanges.data?.length ?? 0,
      label: "date change",
      description: "moved outwards against an agreed baseline",
      to: "/schedule-changes",
      icon: <CalendarClock className="h-4 w-4" />,
    },
    {
      key: "leave",
      count: leave.data?.content.length ?? 0,
      label: "leave request",
      description: "not yet approved, so it reduces nobody's capacity",
      to: "/leave",
      icon: <CalendarOff className="h-4 w-4" />,
    },
  ].filter((entry) => entry.count > 0);

  const canApproveSomething =
    can("TIMESHEET_APPROVE") || can("TIMESHEET_REOPEN")
    || can("SCHEDULE_CHANGE_APPROVE") || can("LEAVE_APPROVE");

  return (
    <>
      <PageHeader
        title={`Welcome back, ${firstName}`}
        subtitle={`${user.designation} · ${user.department}`}
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Figure
          label="Assigned to you"
          value={can("WORK_ITEM_VIEW") ? String(open.length) : "—"}
          note={open.length === 1 ? "open item" : "open items"}
          to="/my-work"
        />
        <Figure
          label="Past its date"
          value={String(overdue.length)}
          note={overdue.length === 0 ? "nothing is late" : "needs a new date or a push"}
          tone={overdue.length > 0 ? "amber" : undefined}
          to="/my-work"
        />
        <Figure
          label="Blocked"
          value={String(blocked.length)}
          note={blocked.length === 0 ? "nothing is waiting on anything" : "waiting on something"}
          tone={blocked.length > 0 ? "amber" : undefined}
          to="/my-work"
        />
        {/* "This period", not "this month": under §72's weekly cadence this figure covers one
            week, and a label naming the wrong span makes the number look like a shortfall. */}
        <Figure
          label="Logged this period"
          value={
            can("TIMESHEET_VIEW")
              ? formatHoursAsTime(timesheet.data?.expectation.loggedHours ?? 0)
              : "—"
          }
          note={
            timesheet.data
              ? `of ${formatHoursAsTime(timesheet.data.expectation.expectedHours)} expected`
              : "this period"
          }
          to="/timesheets"
        />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader
            title={
              <span className="flex items-center gap-2">
                <ListTodo className="h-4 w-4 text-brand-600" />
                What you are working on
              </span>
            }
            subtitle="Assigned to you and still open, soonest first."
            action={
              open.length > 0 ? (
                <Link to="/my-work" className="text-sm text-brand-600 hover:underline">
                  All {open.length}
                </Link>
              ) : undefined
            }
          />
          <CardBody className={open.length > 0 ? "p-0" : undefined}>
            {!can("WORK_ITEM_VIEW") ? (
              <p className="text-sm text-ink-600">
                You do not have access to work items.
              </p>
            ) : myWork.isPending ? (
              <p className="text-sm text-ink-500">Loading…</p>
            ) : open.length === 0 ? (
              <p className="text-sm text-ink-600">
                Nothing is assigned to you. Work reaches you from a project's backlog or by
                somebody assigning it directly.
              </p>
            ) : (
              <ul className="divide-y divide-ink-100">
                {[...open]
                  .sort(byDueDate)
                  .slice(0, 5)
                  .map((item) => (
                    <li key={item.id}>
                      <Link
                        to={`/work-items/${item.itemKey}`}
                        className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-ink-50"
                      >
                        <span className="min-w-0">
                          <span className="mr-2 font-mono text-xs text-ink-500">
                            {item.itemKey}
                          </span>
                          <span className="text-sm text-ink-900">{item.title}</span>
                          <span className="mt-0.5 block text-xs text-ink-500">
                            {TYPE_LABELS[item.workItemType]} · {STATUS_LABELS[item.status]}
                            {item.openBlockerCount > 0 && " · blocked"}
                          </span>
                        </span>
                        <span className="shrink-0 text-right">
                          {item.committedDueDate ? (
                            <span
                              className={
                                item.scheduleVarianceDays > 0
                                  ? "tnum text-sm text-amber-700"
                                  : "tnum text-sm text-ink-600"
                              }
                            >
                              {item.committedDueDate}
                            </span>
                          ) : (
                            <span className="text-sm text-ink-400">no date</span>
                          )}
                        </span>
                      </Link>
                    </li>
                  ))}
              </ul>
            )}
          </CardBody>
        </Card>

        {canApproveSomething ? (
          <Card>
            <CardHeader
              title={
                <span className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-brand-600" />
                  Waiting on you
                </span>
              }
              subtitle="Decisions somebody else cannot make."
            />
            <CardBody className={waiting.length > 0 ? "p-0" : undefined}>
              {waiting.length === 0 ? (
                <p className="text-sm text-ink-600">
                  Nothing is waiting for a decision from you.
                </p>
              ) : (
                <ul className="divide-y divide-ink-100">
                  {waiting.map((entry) => (
                    <li key={entry.key}>
                      <Link
                        to={entry.to}
                        className="flex items-center gap-3 px-5 py-3 hover:bg-ink-50"
                      >
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-700">
                          {entry.icon}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="text-sm text-ink-900">
                            <span className="tnum font-semibold">{entry.count}</span>{" "}
                            {entry.count === 1 ? entry.label : `${entry.label}s`}
                          </span>
                          <span className="mt-0.5 block text-xs text-ink-500">
                            {entry.description}
                          </span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>
        ) : (
          <Card>
            <CardHeader
              title={
                <span className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-brand-600" />
                  Your timesheet
                </span>
              }
              subtitle="Every actual figure in the system comes from here."
            />
            <CardBody>
              <TimesheetSummary timesheet={timesheet.data} canView={can("TIMESHEET_VIEW")} />
            </CardBody>
          </Card>
        )}
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader
            title={
              <span className="flex items-center gap-2">
                <Users className="h-4 w-4 text-brand-600" />
                Your teams today
              </span>
            }
            subtitle="Membership is recorded with dates, so this is today's answer."
          />
          <CardBody>
            {user.teams.length === 0 ? (
              <p className="text-sm text-ink-600">
                You are not currently in a team. That is today's answer rather than a permanent
                one — membership is held against the dates it applies.
              </p>
            ) : (
              <ul className="space-y-3">
                {user.teams.map((team) => (
                  <li key={team.membershipId} className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-ink-900">{team.teamName}</p>
                      <p className="text-xs text-ink-500">
                        Since {team.effectiveFrom}
                        {team.membershipPercentage != null
                          && ` · ${team.membershipPercentage}%`}
                      </p>
                    </div>
                    <StatusBadge tone="blue">{team.role}</StatusBadge>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title={
              <span className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-brand-600" />
                Your access
              </span>
            }
            subtitle="The server checks every permission on each request."
          />
          <CardBody>
            <p className="text-xs uppercase tracking-wide text-ink-500">Roles</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {user.roles.map((role) => (
                <StatusBadge key={role} tone="violet">
                  {role.replace(/_/g, " ")}
                </StatusBadge>
              ))}
            </div>
            <p className="mt-4 text-xs uppercase tracking-wide text-ink-500">Permissions</p>
            <p className="font-display text-2xl font-semibold text-ink-900">
              {user.permissions.length}
            </p>
          </CardBody>
        </Card>
      </div>
    </>
  );
}

interface WaitingItem {
  key: string;
  count: number;
  label: string;
  description: string;
  to: string;
  icon: ReactNode;
}

/** Undated work sorts last: it has no deadline to be near, rather than a deadline of never. */
function byDueDate(left: WorkItemSummary, right: WorkItemSummary): number {
  if (!left.committedDueDate) {
    return right.committedDueDate ? 1 : 0;
  }
  if (!right.committedDueDate) {
    return -1;
  }
  return left.committedDueDate.localeCompare(right.committedDueDate);
}

function TimesheetSummary({
  timesheet,
  canView,
}: {
  timesheet: TimesheetView | undefined;
  canView: boolean;
}) {
  if (!canView) {
    return <p className="text-sm text-ink-600">You do not have access to timesheets.</p>;
  }
  if (!timesheet) {
    return <p className="text-sm text-ink-500">Loading…</p>;
  }

  const { expectation } = timesheet;

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm text-ink-600">This month</span>
        <span className="tnum text-sm text-ink-900">
          {formatHoursAsTime(expectation.loggedHours)} of{" "}
          {formatHoursAsTime(expectation.expectedHours)}
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-ink-100">
        <div
          className="h-full rounded-full bg-brand-500"
          style={{
            width: `${Math.min(
              100,
              Math.round(
                ((expectation.loggedHours ?? 0) / Math.max(1, expectation.expectedHours ?? 1))
                  * 100,
              ),
            )}%`,
          }}
        />
      </div>
      <p className="text-sm text-ink-600">
        {expectation.complete ? (
          "This period is complete."
        ) : (
          <>
            {formatHoursAsTime(expectation.missingHours)} still to account for.{" "}
            <Link to="/timesheets" className="text-brand-600 hover:underline">
              Fill it in
            </Link>
            .
          </>
        )}
      </p>
    </div>
  );
}

function Figure({
  label,
  value,
  note,
  tone,
  to,
}: {
  label: string;
  value: string;
  note: string;
  tone?: "amber";
  to: string;
}) {
  return (
    <Link
      to={to}
      className="rounded-xl border border-ink-200 bg-surface px-4 py-3 transition-colors hover:border-brand-300"
    >
      <p className="text-xs text-ink-500">{label}</p>
      <p
        className={
          "tnum mt-1 font-display text-2xl font-semibold "
          + (tone === "amber" ? "text-amber-700" : "text-ink-900")
        }
      >
        {value}
      </p>
      <p className="mt-0.5 text-xs text-ink-500">{note}</p>
    </Link>
  );
}
